import { RefObject, useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import Player from '../../player';
import { getPreviewHeight } from '../helpers';
import { uploadedImagesStyles } from 'components/uploaded-images/styles/styles';
import PlayIcon from 'assets/icons/play-icon.svg';

const MAX_VIDEO_HEIGHT = 500;

interface PlayerWithCachedHeight {
  containerWidth: number;
  uploadId: string;
  url: string;
  containerRef: RefObject<HTMLDivElement>;
  previewUrl?: string;
  videoHeightCacheRef?: RefObject<Record<string, number>>;
}

export const PlayerWithCachedHeight = ({
  containerWidth,
  uploadId,
  previewUrl,
  url,
  containerRef,
  videoHeightCacheRef,
}: PlayerWithCachedHeight) => {
  const [isPlaying, setIsPlaying] = useState(false);
  
  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);
  const handleStop = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const [height] = useState<number | null>(() => {
    const cached = uploadId && videoHeightCacheRef?.current?.[uploadId]
      ? videoHeightCacheRef.current[uploadId]
      : null;
    return cached ? Math.min(cached, MAX_VIDEO_HEIGHT) : null;
  });

  useEffect(() => {
    if (
      !containerWidth ||
      !uploadId ||
      !previewUrl ||
      !videoHeightCacheRef ||
      videoHeightCacheRef.current?.[uploadId]
    ) {
      return;
    }

    getPreviewHeight(previewUrl, containerWidth)
      .then((calculatedHeight) => {
        videoHeightCacheRef.current[uploadId] = Math.min(calculatedHeight, MAX_VIDEO_HEIGHT);
      })
      .catch(() => {
        // Preview image load failed — will use default height
      });
  }, [containerWidth, uploadId, previewUrl, videoHeightCacheRef]);

  useEffect(() => {
    setIsPlaying(false);
  }, [url]);

  return (
    <Grid
      ref={containerRef}
      sx={{
        ...uploadedImagesStyles.bigImageContainer,
        position: 'relative',
        maxHeight: `${MAX_VIDEO_HEIGHT}px`,
        overflow: 'hidden',
        borderRadius: '12px',
        '& video': {
          width: '100%',
          maxHeight: `${MAX_VIDEO_HEIGHT}px`,
          objectFit: 'cover',
          borderRadius: '12px',
        },
      }}
      style={height ? { height: `${height}px` } : undefined}
    >
      {containerWidth ? (
        <>
          <Player
            url={url}
            controls={true}
            onPlay={handlePlay}
            onPause={handleStop}
            onEnded={handleStop}
          />
          {!isPlaying ? (
            <Grid
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Grid
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PlayIcon />
              </Grid>
            </Grid>
          ) : null}
        </>
      ) : null}
    </Grid>
  );
};
